import {
  type SaveCardCheckoutNote,
  useCardCheckoutNoteBeforeConfirm,
} from "../useCardCheckoutNoteBeforeConfirm";
import { foldProvincialChamberFeeForDisplay } from "../provincialChamberFeeDisplay";
import { chamberDateInputToUtcMilli } from "../invoiceDueDate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpManageInvoiceRowFieldsFragment,
  CmpOperationalEventTypeEnum,
  CmpPaymentStatusEnum,
  GetUserQuery,
  useGetCmpInvoicePresentationDefaultsQuery,
} from "../../../../graphql/types";
import { commonStyles } from "../../../common/commonStyles";
import ActionSuccessDialog from "../../../common/dashboard/ActionSuccessDialog";
import CalendarDateInput, { isCalendarDateValue } from "../../../common/dashboard/CalendarDateInput";
import { formatUtcCalendarDate } from "../../../common/dashboard/formatters";
import { CmpEventCardCheckoutRestriction } from "../invoiceCardCheckoutRouting";
import { getInvoicePaymentCapabilities } from "../invoicePaymentCapabilities";
import ProcessPaymentDialogShell from "../ProcessPaymentDialogShell";
import SecureCardCheckoutHandoff, {
  SecureCardCheckoutHandoffData,
  SecureCardCheckoutStatus,
} from "../SecureCardCheckoutHandoff";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
  invoiceProcessPaymentFieldSx,
} from "../InvoiceDesignSystem";
import ManageInvoiceActionConfirmationDialog from "./ManageInvoiceActionConfirmationDialog";
import {
  getStaffPaymentMethodOptions,
  type StaffRecordablePaymentMethod,
} from "../staffPaymentMethods";
import { getCreditedByDocumentLine } from "../invoiceCreditLinks";
import {
  InvoicePreviewPrintStyles,
  InvoicePreviewZoomBar,
  useInvoicePreviewFitScale,
} from "./InvoicePreviewChrome";
import {
  formatCurrency,
  formatInvoiceCoveragePeriodText,
  formatInvoiceDocumentDate,
  getInvoiceDocumentStatusLabel,
  getInvoiceDocumentTitle,
  getInvoicePreviewActions,
  getInvoiceWatermark,
  getLatestSuccessfulInvoicePayment,
  getRecordedPaymentMethodLabel,
  getLocalDateInputValue,
  isCreditedInvoiceDisplayState,
  localDateInputToUtcMilli,
  InvoiceDocumentMode,
  InvoicePreviewAction,
  InvoicePreviewPendingAction,
} from "../invoiceFormatting";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
} from "../../../../utils/userFacingError";

// A manual payment may have saved before the error, so staff check first.
const PAYMENT_RECORD_UNCONFIRMED_TEXT = `We could not confirm that the payment was recorded. Refresh the page and check the invoice before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;

export {
  InvoicePreviewPrintStyles,
  InvoicePreviewZoomBar,
  useInvoicePreviewFitScale,
} from "./InvoicePreviewChrome";

type InvoiceRow = CmpManageInvoiceRowFieldsFragment;
type InvoiceDetail = CmpInvoiceDetailFieldsFragment;

// Saved event names can already start with the code ("GF - Golf Day").
export const eventNameStartsWithCode = (eventName: string, eventCode: string) =>
  new RegExp(`^${eventCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[-–—]`, "i").test(eventName.trim());

type ChamberInfo = GetUserQuery["getUser"]["chamberInfo"];

export const invoicePreviewPageWidth = 1070;
export const invoicePreviewCloseButtonSize = 28;

export const printInvoicePreviewDocument = () => {
  if (typeof window === "undefined" || typeof window.print !== "function") {
    return false;
  }

  window.print();
  return true;
};

export type InvoiceDocumentSnapshot = Readonly<{
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  contactEmail?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactTitle?: string | null;
  country?: string | null;
  customerName?: string | null;
  postalCode?: string | null;
  province?: string | null;
}>;

export type InvoiceDraftDocumentLineItem = Readonly<{
  code?: string | null;
  description: string;
  discountPercentage?: number | null;
  id: string;
  quantity: number;
  totalInCents: number;
  unitPriceInCents: number;
  /** Design (15) line note, e.g. "Includes 4 complimentary tickets". */
  note?: string | null;
}>;

export type InvoiceDraftDocumentPaymentMethod = Readonly<{
  enabled?: boolean | null;
  label: string;
}>;

export type InvoiceDraftDocumentData = Readonly<{
  billToSnapshot?: InvoiceDocumentSnapshot | null;
  billingSnapshot: InvoiceDocumentSnapshot;
  // The member's company details for Sold To; billingSnapshot when absent.
  soldToSnapshot?: InvoiceDocumentSnapshot | null;
  customerName: string;
  dueDate: string;
  // Optional event code. The event line reads "{code} - {name}" when present.
  eventCode?: string | null;
  eventName?: string | null;
  eventStartAtUtcMilli?: number | null;
  eventTimeZone?: string | null;
  id?: string;
  invoiceDate: string;
  invoiceKind: CmpInvoiceKindEnum;
  invoiceNumber?: string | null;
  lineItems: readonly InvoiceDraftDocumentLineItem[];
  manualPaymentMethods?: readonly InvoiceDraftDocumentPaymentMethod[];
  publicMemo?: string | null;
  referencePurchaseOrder?: string | null;
  subtotalInCents: number;
  taxInCents: number;
  taxLabel: string;
  /** Design (15) note under the tax line (sponsorship invoices). */
  taxNote?: string | null;
  taxRegistrationNumber?: string | null;
  totalInCents: number;
}>;

type InvoicePaperLineItem =
  | NonNullable<InvoiceDetail["invoice"]["lineItems"]>[number]
  | InvoiceDraftDocumentLineItem;

const getInvoicePaperLineItemCode = (lineItem: InvoicePaperLineItem) =>
  "totalInCents" in lineItem ? lineItem.code : lineItem.itemCode;

// Line totals are before tax, so the lines add up to Subtotal and tax shows
// once in the summary (Claude Design and the draft preview). A saved line's
// subtotal is its quantity x unit price; discount lines are negative.
const getInvoicePaperLineItemTotal = (lineItem: InvoicePaperLineItem) =>
  "totalInCents" in lineItem
    ? lineItem.totalInCents
    : lineItem.subtotalAmountInCents;

type CreditInvoiceRecordFields = {
  creditForInvoiceId?: string | null;
  creditForInvoiceNumber?: string | null;
  creditedAmountInCents?: number | null;
  creditedAtUtcMilli?: number | null;
  creditReason?: string | null;
  creditReasonExplanation?: string | null;
  creditRefundedAtUtcMilli?: number | null;
  creditRefundMethodLabel?: string | null;
  creditRefundReference?: string | null;
  invoiceDirection?: string | null;
};

export type InvoiceDocumentRow = Pick<
  InvoiceRow,
  | "customerName"
  | "displayState"
  | "id"
  | "invoiceKind"
  | "invoiceNumber"
  | "issuedAtUtcMilli"
> &
  Partial<
    Pick<
      InvoiceRow,
      | "availableActions"
      | "balanceDueInCents"
      | "daysPastDue"
      | "dueAtUtcMilli"
      | "payerDataWarning"
      | "paymentMethodLabel"
      | "paymentPlan"
      | "paymentRules"
      | "sourceLabel"
      | "totalAmountInCents"
    >
  >;

export type RecordInvoicePaymentInput = {
  amountInCents: number;
  note?: string;
  paymentMethodLabel: string;
  receivedAtUtcMilli?: number;
  reference?: string;
};

const invoiceBlue = "#1684FF";
const invoiceGreen = "#16B983";
const invoiceRed = "#F31723";
const creditCardPaymentMethodLabel = "Credit Card";
const approvedPaymentMethodOrder = [
  "Credit Card",
  "E-Transfer",
  "Cheque",
  "Direct Deposit",
  "Cash",
] as const;

type PaymentMethodOption = {
  displayLabel: string;
  isCreditCard: boolean;
  mutationLabel: string;
};

const formatDateInput = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return getLocalDateInputValue();
  }

  return getLocalDateInputValue(new Date(utcMilli));
};

const formatCurrencyInput = (amountInCents: number) =>
  (amountInCents / 100).toFixed(2);

const formatDraftCalendarDate = (dateInput: string) => {
  const timestamp = chamberDateInputToUtcMilli({ dateInput, timeZone: "UTC" });
  return timestamp === undefined ? "Date unavailable" : formatUtcCalendarDate(timestamp);
};

export const formatSavedEventDocumentDate = ({
  eventStartAtUtcMilli,
  eventTimeZone,
}: {
  eventStartAtUtcMilli?: number | null;
  eventTimeZone?: string | null;
}): string => {
  if (
    !Number.isFinite(eventStartAtUtcMilli) ||
    !eventStartAtUtcMilli ||
    !eventTimeZone?.trim()
  ) {
    return "Event date unavailable";
  }

  try {
    return new Intl.DateTimeFormat("en-CA", {
      day: "numeric",
      month: "long",
      timeZone: eventTimeZone,
      year: "numeric",
    }).format(new Date(eventStartAtUtcMilli));
  } catch {
    return "Event date unavailable";
  }
};

const parseCurrencyInput = (value: string) =>
  Math.max(0, Math.round((Number(value.replace(/[^0-9.]/g, "")) || 0) * 100));

const getCreditInvoiceFields = (detail?: InvoiceDetail | null) =>
  (detail?.invoice || null) as
    (InvoiceDetail["invoice"] & CreditInvoiceRecordFields) | null;

const isCreditInvoiceRecord = (detail?: InvoiceDetail | null) =>
  getCreditInvoiceFields(detail)?.invoiceDirection === "CREDIT";

const formatCreditReason = (reason?: string | null) => {
  const normalizedReason = (reason || "").trim();

  if (!normalizedReason) {
    return "Credit issued";
  }

  return normalizedReason
    .toLowerCase()
    .split("_")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ")
    .replace("Sponsorship Advertising", "Sponsorship/Advertising");
};

const formatDirectedCreditCurrency = (
  amountInCents: number,
  creditDirection: boolean,
) =>
  creditDirection
    ? `${amountInCents ? "-" : ""}${formatCurrency(Math.abs(amountInCents))}`
    : formatCurrency(amountInCents);

const normalizeManualMethodLabel = (label: string) => {
  const normalized = label.trim().toLowerCase();

  if (normalized.includes("direct")) {
    return "Direct Deposit";
  }

  if (normalized.includes("transfer")) {
    return "E-Transfer";
  }

  if (normalized.includes("cheque") || normalized.includes("check")) {
    return "Cheque";
  }

  if (normalized.includes("cash")) {
    return "Cash";
  }

  return label.trim();
};

const getInvoiceLineItems = (detail?: InvoiceDetail | null) => {
  if (detail?.lineItems?.length) {
    return detail.lineItems;
  }

  return detail?.invoice.lineItems || [];
};

const sumLineItems = (
  detail: InvoiceDetail | null | undefined,
  field: "subtotalAmountInCents" | "taxAmountInCents" | "totalAmountInCents",
) =>
  getInvoiceLineItems(detail).reduce((total, item) => total + item[field], 0);

const getInvoiceSubtotal = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) =>
  detail?.invoice.subtotalAmountInCents ??
  sumLineItems(detail, "subtotalAmountInCents") ??
  row?.totalAmountInCents ??
  0;

const getInvoiceTax = (detail?: InvoiceDetail | null) =>
  detail?.invoice.taxAmountInCents ??
  sumLineItems(detail, "taxAmountInCents") ??
  0;

const getInvoiceTotal = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) =>
  detail?.invoice.totalAmountInCents ??
  sumLineItems(detail, "totalAmountInCents") ??
  row?.totalAmountInCents ??
  0;

const getAmountDue = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => detail?.invoice.amountDueInCents ?? row?.balanceDueInCents ?? 0;

const getPaymentPlan = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => detail?.invoice.paymentPlan || row?.paymentPlan || null;

const getPaymentRules = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => detail?.invoice.paymentRules || row?.paymentRules || null;

const getBillingSnapshot = (detail?: InvoiceDetail | null) =>
  detail?.billingSnapshot || detail?.invoice.billingSnapshot || null;

const getBillToSnapshot = (detail?: InvoiceDetail | null) =>
  detail?.invoice.billToSnapshot || getBillingSnapshot(detail);

// Sold To prints the member's company details when the backend sends them
// (member with a separate Billing/Mailing Address).
const getSoldToSnapshot = (detail?: InvoiceDetail | null) =>
  detail?.soldToSnapshot || getBillingSnapshot(detail);

const getAddressLines = (
  snapshot: InvoiceDocumentSnapshot | null | undefined,
) =>
  [
    snapshot?.addressLine1,
    snapshot?.addressLine2,
    [snapshot?.city, snapshot?.province, snapshot?.postalCode]
      .filter(Boolean)
      .join(" "),
    snapshot?.country,
  ].filter(Boolean);

const getChamberAddressLines = (chamber?: ChamberInfo) =>
  [
    chamber?.address?.streetAddress,
    [
      chamber?.address?.city,
      chamber?.address?.province,
      chamber?.address?.postalCode,
    ]
      .filter(Boolean)
      .join(" "),
  ].filter(Boolean);

const getNextPaymentAmount = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => {
  const plan = getPaymentPlan(detail, row);

  return plan?.nextInstallmentAmountInCents || getAmountDue(detail, row);
};

const formatTaxRatePercentage = (ratePercentage: number) =>
  String(Math.round(ratePercentage * 100) / 100);

const getInvoiceTaxRatePercentage = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => {
  const lineItemTaxRates = Array.from(
    new Set(
      getInvoiceLineItems(detail)
        .map((item) => item.taxRatePercentage)
        .filter((rate) => Number.isFinite(rate) && rate > 0)
        .map((rate) => Math.round(rate * 10000) / 10000),
    ),
  );

  if (lineItemTaxRates.length === 1) {
    return lineItemTaxRates[0];
  }

  const subtotal = getInvoiceSubtotal(detail, row);
  const tax = getInvoiceTax(detail);

  if (!subtotal || !tax) {
    return 0;
  }

  return (tax / subtotal) * 100;
};

const getInvoiceTaxLabel = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
) => {
  const taxRatePercentage = getInvoiceTaxRatePercentage(detail, row);

  return taxRatePercentage > 0
    ? `Tax (${formatTaxRatePercentage(taxRatePercentage)}%)`
    : "Tax";
};

const buildPaymentMethodOptions = (
  detail?: InvoiceDetail | null,
  row?: InvoiceDocumentRow | null,
): PaymentMethodOption[] => {
  const rules = getPaymentRules(detail, row);
  const options = [
    rules?.creditCardAllowed
      ? {
          displayLabel: creditCardPaymentMethodLabel,
          isCreditCard: true,
          mutationLabel: creditCardPaymentMethodLabel,
        }
      : null,
    ...(rules?.allowedManualPaymentMethodLabels || []).map((label) => ({
      displayLabel: normalizeManualMethodLabel(label),
      isCreditCard: false,
      mutationLabel: label,
    })),
  ].filter(Boolean) as PaymentMethodOption[];

  return orderPaymentMethodOptions(options);
};

// Keeps one option per approved method, in the approved order. Methods that
// are not on an invoice document (for example Invoice Me) drop out here.
const orderPaymentMethodOptions = (
  options: readonly PaymentMethodOption[],
): PaymentMethodOption[] => {
  const optionsByDisplayLabel = new Map<string, PaymentMethodOption>();

  options.forEach((option) => {
    if (
      approvedPaymentMethodOrder.includes(
        option.displayLabel as (typeof approvedPaymentMethodOrder)[number],
      ) &&
      !optionsByDisplayLabel.has(option.displayLabel)
    ) {
      optionsByDisplayLabel.set(option.displayLabel, option);
    }
  });

  return approvedPaymentMethodOrder
    .map((label) => optionsByDisplayLabel.get(label))
    .filter((option): option is PaymentMethodOption => Boolean(option));
};

type PaymentCardLine = Readonly<{
  bold?: boolean;
  // The design sets "MAIL OR DELIVER TO:" at 11px; body lines are 11.5px.
  small?: boolean;
  text: string;
}>;

type PaymentCard = Readonly<{
  cta?: string;
  label: string;
  lines: readonly PaymentCardLine[];
}>;

// Claude Design InvoicePreviewModal PaymentOptions copy, per method.
const creditCardPaymentCard: PaymentCard = {
  label: "Pay Credit Card",
  lines: [{ text: "Pay securely online or call chamber via credit card." }],
  cta: "Pay Now",
};

const getManualPaymentCard = ({
  chamber,
  eTransferEmail,
  invoiceNumber,
  label,
}: {
  chamber?: ChamberInfo;
  eTransferEmail?: string | null;
  invoiceNumber: string;
  label: string;
}): PaymentCard => {
  if (label === "E-Transfer") {
    return {
      label: "E-Transfer",
      lines: eTransferEmail
        ? [
            { text: "Send payment to:" },
            { bold: true, text: eTransferEmail },
            ...(invoiceNumber
              ? [{ text: "Reference:" }, { bold: true, text: invoiceNumber }]
              : []),
          ]
        : [{ text: "Please contact the chamber for e-transfer instructions." }],
    };
  }

  if (label === "Cheque") {
    const mailingAddressLines = getChamberAddressLines(chamber).filter(
      (line): line is string => Boolean(line),
    );

    return {
      label: "Cheque",
      lines: [
        { text: "Make payable to:" },
        { bold: true, text: chamber?.name || "the chamber" },
        ...(mailingAddressLines.length
          ? [
              { bold: true, small: true, text: "MAIL OR DELIVER TO:" },
              ...mailingAddressLines.map((line) => ({ text: line })),
            ]
          : []),
      ],
    };
  }

  if (label === "Direct Deposit") {
    return {
      label: "Direct Deposit",
      lines: [
        { text: "Please contact the chamber for direct deposit instructions." },
      ],
    };
  }

  if (label === "Cash") {
    return {
      label: "Cash",
      lines: [{ text: "Please contact the chamber to arrange cash payment." }],
    };
  }

  return {
    label,
    lines: [{ text: "Please contact the chamber to arrange this payment method." }],
  };
};

const getPaymentCardsForMethods = ({
  chamber,
  eTransferEmail,
  invoiceNumber,
  methods,
}: {
  chamber?: ChamberInfo;
  eTransferEmail?: string | null;
  invoiceNumber: string;
  methods: readonly PaymentMethodOption[];
}): PaymentCard[] =>
  methods.map((method) =>
    method.isCreditCard
      ? creditCardPaymentCard
      : getManualPaymentCard({
          chamber,
          eTransferEmail,
          invoiceNumber,
          label: method.displayLabel,
        }),
  );

// The Pay Credit Card column follows the payment rules: it renders whenever
// card payment is allowed, and a draft shows it with Pay Now disabled.
const getPaymentOptionCards = ({
  chamber,
  detail,
  eTransferEmail,
  row,
}: {
  chamber?: ChamberInfo;
  detail?: InvoiceDetail | null;
  eTransferEmail?: string | null;
  row?: InvoiceDocumentRow | null;
}): PaymentCard[] =>
  getPaymentCardsForMethods({
    chamber,
    eTransferEmail,
    invoiceNumber: row?.invoiceNumber || detail?.invoice.invoiceNumber || "",
    methods: buildPaymentMethodOptions(detail, row),
  });

// An unsaved invoice uses the same columns, order and copy as the issued
// invoice, so the preview shows what the customer will get.
const getDraftPaymentOptionCards = (
  draftDocument: InvoiceDraftDocumentData,
  chamber?: ChamberInfo,
  eTransferEmail?: string | null,
): PaymentCard[] =>
  getPaymentCardsForMethods({
    chamber,
    eTransferEmail,
    invoiceNumber: draftDocument.invoiceNumber || "",
    // Callers list "Credit Card" only when card payment will be allowed on
    // the issued invoice, so the draft column never promises a card option.
    methods: orderPaymentMethodOptions(
      (draftDocument.manualPaymentMethods || [])
        .filter((method) => method.enabled !== false)
        .map((method) => {
          const isCreditCard =
            method.label.trim() === creditCardPaymentMethodLabel;

          return {
            displayLabel: isCreditCard
              ? creditCardPaymentMethodLabel
              : normalizeManualMethodLabel(method.label),
            isCreditCard,
            mutationLabel: method.label,
          };
        }),
    ),
  });

/**
 * Payment methods for an unsaved staff ticket, sponsorship or custom invoice.
 * These invoices use the standard payment rules: card when Stripe is ready,
 * then every enabled offline method.
 */
export const getStaffInvoiceDraftPaymentMethods = ({
  creditCardPaymentsReady,
  manualPaymentMethods,
}: {
  creditCardPaymentsReady: boolean;
  manualPaymentMethods: readonly InvoiceDraftDocumentPaymentMethod[];
}): InvoiceDraftDocumentPaymentMethod[] => [
  ...(creditCardPaymentsReady
    ? [{ enabled: true, label: creditCardPaymentMethodLabel }]
    : []),
  ...manualPaymentMethods
    .filter((method) => method.enabled !== false)
    .map((method) => ({ enabled: true, label: method.label })),
];

const invoiceActionButtonColors: Record<
  InvoicePreviewAction["color"],
  { background: string; hover: string }
> = {
  dark: { background: "#0A0A0A", hover: "#26282C" },
  gold: { background: commonStyles.cpaGold, hover: "#C88B16" },
  green: { background: invoiceGreen, hover: "#12A875" },
  red: { background: invoiceRed, hover: "#D90F1A" },
};

const InvoiceActionButton = ({
  children,
  color = "gold",
  disabled,
  icon,
  onClick,
}: {
  children: React.ReactNode;
  color?: InvoicePreviewAction["color"];
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}) => (
  <Button
    disabled={disabled}
    onClick={onClick}
    startIcon={icon}
    sx={{
      backgroundColor: invoiceActionButtonColors[color].background,
      borderRadius: "8px",
      color: "white",
      fontFamily: invoiceDesignFontFamily,
      fontSize: 15,
      fontWeight: 600,
      minHeight: 46,
      px: 3.25,
      textTransform: "none",
      "&:hover": {
        backgroundColor: invoiceActionButtonColors[color].hover,
      },
    }}
  >
    {children}
  </Button>
);

// Claude Design InvoicePreviewModal glyphs (12px blue phone and mail icons,
// white send glyph on the Email button).
const invoiceDocumentIconBlue = "#0091FF";

const InvoicePhoneIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    stroke={invoiceDocumentIconBlue}
    strokeLinecap="round"
    strokeWidth="2.2"
    style={{ flexShrink: 0 }}
    viewBox="0 0 24 24"
    width="12"
  >
    <path d="M5 3h3l2 5-2 1a11 11 0 0 0 5 5l1-2 5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 5a2 2 0 0 1 2-2z" />
  </svg>
);

const InvoiceMailIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    stroke={invoiceDocumentIconBlue}
    strokeLinecap="round"
    strokeWidth="2.2"
    style={{ flexShrink: 0 }}
    viewBox="0 0 24 24"
    width="12"
  >
    <rect height="14" rx="2" width="18" x="3" y="5" />
    <path d="m3.5 6.5 8.5 6 8.5-6" />
  </svg>
);

const InvoiceSendIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.7"
    viewBox="0 0 24 24"
    width="16"
  >
    <path d="M21 3 3 10.5l7 2.5 2.5 7z" />
  </svg>
);

const SnapshotBlock = ({
  label,
  snapshot,
  fallbackName,
}: {
  fallbackName: string;
  label: string;
  snapshot: InvoiceDocumentSnapshot | null | undefined;
}) => (
  <div className="min-w-0 break-words text-[13.5px] leading-[1.55] text-[#111827]">
    <div className="mb-3 text-[11.5px] font-bold uppercase tracking-[0.06em]">
      {label}
    </div>
    <div className="mb-1 font-bold">
      {snapshot?.customerName || fallbackName}
    </div>
    {snapshot?.contactName ? (
      <div>
        Attn: {snapshot.contactName}
        {snapshot.contactTitle?.trim() ? `, ${snapshot.contactTitle.trim()}` : ""}
      </div>
    ) : null}
    {getAddressLines(snapshot).map((line) => (
      <div key={line}>{line}</div>
    ))}
    {snapshot?.contactEmail ? (
      <div className="mt-3.5">{snapshot.contactEmail}</div>
    ) : null}
    {snapshot?.contactPhone ? <div>{snapshot.contactPhone}</div> : null}
  </div>
);

type InvoicePresentation = Pick<
  ReturnType<typeof useGetCmpInvoicePresentationDefaultsQuery>,
  "data" | "error" | "loading"
>;

// Billing inquiries block (design D-IN11): the Chamber Setup invoice billing
// contact name, email and phone, falling back to the chamber profile.
const InvoiceBillingInquiryContact = ({
  presentation,
  chamberPhone,
}: {
  presentation: InvoicePresentation;
  chamberPhone?: string | null;
}) => {
  const { data, error, loading } = presentation;

  if (error) {
    return (
      <div role="alert">
        Billing contact could not be loaded. Reopen the preview to try again.
      </div>
    );
  }

  if (loading || !data) {
    return <div role="status">Loading billing contact...</div>;
  }

  const defaults = data.getCmpInvoicePresentationDefaults;
  const phone = defaults.billingInquiryPhone?.trim() || chamberPhone?.trim() || "";
  return (
    <>
      {defaults.billingInquiryContactName?.trim() ? (
        <div>{defaults.billingInquiryContactName.trim()}</div>
      ) : null}
      <div className="flex items-center gap-[9px]">
        <InvoiceMailIcon />
        {defaults.billingInquiryEmail}
      </div>
      {phone ? (
        <div className="flex items-center gap-[9px]">
          <InvoicePhoneIcon />
          {phone}
        </div>
      ) : null}
    </>
  );
};

// Exported per SCR-MC-01 so the Create Member invoice preview can reuse the
// master invoice document (PAR-MC-32 cross-file parity, module 10).
type InvoicePaperProps = {
  canPayNow?: boolean;
  chamber?: ChamberInfo;
  detail?: InvoiceDetail | null;
  documentMode?: InvoiceDocumentMode;
  draftDocument?: InvoiceDraftDocumentData | null;
  onPayNow?: () => void;
  row: InvoiceDocumentRow;
};

const InvoicePaperWithPresentation = (props: InvoicePaperProps & { chamber: NonNullable<ChamberInfo> }) => {
  const presentation = useGetCmpInvoicePresentationDefaultsQuery({
    variables: { input: { chamberId: props.chamber.id } },
    fetchPolicy: "cache-and-network",
  });
  return <InvoicePaperContent {...props} presentation={presentation} />;
};

export const InvoicePaper = (props: InvoicePaperProps) => props.chamber?.id
  ? <InvoicePaperWithPresentation {...props} chamber={props.chamber} />
  : <InvoicePaperContent {...props} />;

const InvoicePaperContent = ({
  canPayNow = false,
  chamber,
  detail,
  documentMode = "INVOICE",
  draftDocument,
  onPayNow,
  presentation,
  row,
}: InvoicePaperProps & { presentation?: InvoicePresentation }) => {
  const eTransferEmail = !presentation?.loading && !presentation?.error
    ? presentation?.data?.getCmpInvoicePresentationDefaults.eTransferEmail
    : undefined;
  const formatDate = (utcMilli?: number | null) => {
    if (!utcMilli) return "-";
    if (!presentation || presentation.error) return "Date unavailable";
    if (presentation.loading || !presentation.data) return "Loading date...";
    if (!presentation.data.getCmpInvoicePresentationDefaults.timeZone) return "Date unavailable";
    return formatInvoiceDocumentDate(
      utcMilli,
      presentation.data.getCmpInvoicePresentationDefaults.timeZone,
    );
  };
  const soldToSnapshot = draftDocument
    ? draftDocument.soldToSnapshot || draftDocument.billingSnapshot
    : getSoldToSnapshot(detail);
  const billToSnapshot =
    draftDocument?.billToSnapshot ||
    draftDocument?.billingSnapshot ||
    getBillToSnapshot(detail);
  // Saved invoices follow "Show separate on invoice" (the draft preview
  // shows exactly the lines being edited). Totals are unchanged.
  const showProvincialChamberFeeSeparately =
    !presentation?.loading && !presentation?.error
      ? presentation?.data?.getCmpInvoicePresentationDefaults
          .showProvincialChamberFeeSeparately
      : undefined;
  const lineItems: readonly InvoicePaperLineItem[] =
    draftDocument?.lineItems ||
    foldProvincialChamberFeeForDisplay({
      lineItems: getInvoiceLineItems(detail),
      showSeparately: showProvincialChamberFeeSeparately,
    });
  const subtotal =
    draftDocument?.subtotalInCents ?? getInvoiceSubtotal(detail, row);
  const tax = draftDocument?.taxInCents ?? getInvoiceTax(detail);
  const taxLabel = draftDocument?.taxLabel || getInvoiceTaxLabel(detail, row);
  const taxNote = draftDocument ? draftDocument.taxNote || null : detail?.taxNote || null;
  const total = draftDocument?.totalInCents ?? getInvoiceTotal(detail, row);
  const amountDue = draftDocument?.totalInCents ?? getAmountDue(detail, row);
  const resolvedDisplayState = draftDocument
    ? CmpInvoiceDisplayStateEnum.Draft
    : detail?.displayState || row.displayState;
  const creditInvoiceFields = getCreditInvoiceFields(detail);
  const isCreditDocument = isCreditInvoiceRecord(detail);
  // A source invoice names its credit notes, one line each, in the notes
  // area (the backend PDF prints the same line). Credit notes keep their own
  // "Credit for invoice" line.
  const creditedByLines =
    draftDocument || isCreditDocument || documentMode !== "INVOICE"
      ? []
      : (detail?.invoice.creditedByInvoices || [])
          .filter((link) => link.invoiceNumber.trim())
          .map((link) =>
            getCreditedByDocumentLine({
              formatAmount: formatCurrency,
              formatDate,
              link,
            }),
          );
  const creditedAmountInCents =
    creditInvoiceFields?.creditedAmountInCents || total;
  const statusLabel =
    documentMode === "PAYMENT_RECEIPT"
      ? "Paid"
      : getInvoiceDocumentStatusLabel({
          daysPastDue: row.daysPastDue,
          displayState: resolvedDisplayState,
        });
  const watermark = getInvoiceWatermark(resolvedDisplayState);
  const paymentPlan = getPaymentPlan(detail, row);
  const payments = detail?.payments || [];
  const latestPayment = getLatestSuccessfulInvoicePayment(payments);
  const paymentCards = draftDocument
    ? getDraftPaymentOptionCards(draftDocument, chamber, eTransferEmail)
    : getPaymentOptionCards({
        chamber,
        detail,
        eTransferEmail,
        row,
      });
  const isDraftDocument =
    resolvedDisplayState === CmpInvoiceDisplayStateEnum.Draft;
  const isPaidInvoice =
    resolvedDisplayState === CmpInvoiceDisplayStateEnum.Paid;
  const isVoidedInvoice =
    resolvedDisplayState === CmpInvoiceDisplayStateEnum.Voided;
  const isCreditedInvoice = isCreditedInvoiceDisplayState(resolvedDisplayState);
  const latestCredit = [...(detail?.refunds || [])].sort(
    (first, second) =>
      (second.completedAtUtcMilli || second.createdAtUtcMilli || 0) -
      (first.completedAtUtcMilli || first.createdAtUtcMilli || 0),
  )[0];
  // Spec §4: the stamped invoiceKind classifies the document — the add-on
  // invoice shares sourceKind MEMBER_APPLICATION with the membership
  // application invoice, so kind must win (mirrors the backend PDF).
  const invoiceKind =
    draftDocument?.invoiceKind ??
    detail?.invoice.invoiceKind ??
    row.invoiceKind;
  const isAddOnInvoice = invoiceKind === CmpInvoiceKindEnum.AddOn;
  const isMembershipInvoice =
    invoiceKind === CmpInvoiceKindEnum.Application ||
    invoiceKind === CmpInvoiceKindEnum.Renewal;
  const isGroupInsuranceInvoice =
    getPaymentRules(detail, row)?.ruleKind ===
    CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;
  // Coverage fields only exist on newly built Application/AddOn invoices;
  // legacy invoices return null and omit the validity sentences gracefully.
  const hasCoveragePeriod =
    !!detail?.invoice.coveragePeriodStartUtcMilli &&
    !!detail?.invoice.coveragePeriodEndUtcMilli;
  const coveragePeriodText = formatInvoiceCoveragePeriodText({
    coveragePeriodEndUtcMilli: detail?.invoice.coveragePeriodEndUtcMilli,
    coveragePeriodStartUtcMilli: detail?.invoice.coveragePeriodStartUtcMilli,
    invoiceKind,
  });
  const isEventInvoice =
    invoiceKind === CmpInvoiceKindEnum.EventRegistration ||
    invoiceKind === CmpInvoiceKindEnum.EventSponsorship;
  const eventName = isEventInvoice
    ? draftDocument?.eventName?.trim() ||
      (
        detail?.invoice as InvoiceDetail["invoice"] & {
          eventNameSnapshot?: string | null;
        }
      )?.eventNameSnapshot?.trim() ||
      "Event name unavailable"
    : "";
  const eventCode = isEventInvoice
    ? draftDocument?.eventCode?.trim() || detail?.eventCode?.trim() || ""
    : "";
  const eventDate = isEventInvoice
    ? formatSavedEventDocumentDate({
        eventStartAtUtcMilli:
          draftDocument?.eventStartAtUtcMilli ??
          detail?.invoice.eventStartAtUtcMilliSnapshot,
        eventTimeZone:
          draftDocument?.eventTimeZone ?? detail?.invoice.eventTimeZoneSnapshot,
      })
    : "";
  const hasEventDate =
    Boolean(eventDate) && eventDate !== "Event date unavailable";
  const referencePurchaseOrder =
    draftDocument?.referencePurchaseOrder?.trim() ||
    detail?.invoice.referencePurchaseOrder?.trim() ||
    "";
  const receiptAmountInCents = latestPayment?.amountInCents || total;
  const isPaidMembershipDocument =
    isPaidInvoice || documentMode === "PAYMENT_RECEIPT";
  // Claude Design payment box: received (paid or receipt), direct deposit
  // header only (group insurance), payment options (draft and awaiting), or
  // no box (credited and voided).
  const paymentBoxKind: "none" | "received" | "insurance" | "options" =
    documentMode === "PAYMENT_RECEIPT" || isPaidInvoice
      ? "received"
      : isCreditedInvoice || isVoidedInvoice
        ? "none"
        : isGroupInsuranceInvoice
          ? "insurance"
          : "options";
  const taxRegistrationNumber =
    draftDocument?.taxRegistrationNumber || detail?.taxRegistrationNumber || "";
  const showDiscountColumn = Boolean(
    draftDocument?.lineItems.some(
      (lineItem) => lineItem.discountPercentage != null,
    ),
  );
  const lineItemGridClassName = showDiscountColumn
    ? "grid-cols-[1fr_2fr_0.7fr_1.1fr_0.9fr_1.1fr]"
    : "grid-cols-[1fr_2.3fr_0.8fr_1.2fr_1.2fr]";

  return (
    <div
      className="relative mx-auto w-full max-w-[1070px] overflow-hidden rounded-[14px] bg-white p-[52px] shadow-[0_2px_10px_rgba(0,0,0,0.05)]"
      data-invoice-paper-root
      style={{ fontFamily: invoiceDesignFontFamily }}
    >
      {watermark ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
          <div
            className={`rotate-[-38deg] text-[120px] font-extrabold uppercase tracking-wide opacity-45 sm:text-[170px] ${
              resolvedDisplayState === CmpInvoiceDisplayStateEnum.Draft
                ? "text-gray-400"
                : "text-[#F31723]"
            }`}
          >
            {watermark}
          </div>
        </div>
      ) : null}

      <div className="relative z-10">
        <div className="flex flex-wrap justify-between gap-[30px]">
          <div className="text-[13.5px] leading-[1.55] text-[#111827]">
            <div className="text-[15px] font-bold">
              {chamber?.logo ? (
                <img
                  alt={`${chamber.name} logo`}
                  className="mb-3 max-h-16 max-w-[220px]"
                  src={chamber.logo}
                />
              ) : (
                "{Chamber Logo}"
              )}
            </div>
            <div className="mt-1 text-[17px] font-bold">
              {chamber?.name || "Chamber of Commerce"}
            </div>
            {getChamberAddressLines(chamber).map((line) => (
              <div key={line}>{line}</div>
            ))}
            {chamber?.phoneNumber ? (
              <div className="mt-2 flex items-center gap-[7px] text-[12.5px]">
                <InvoicePhoneIcon />
                {chamber.phoneNumber}
              </div>
            ) : null}
          </div>

          <div className="flex min-w-[300px] flex-col items-end gap-1 text-right">
            <div
              className="text-[48px] font-bold leading-none tracking-[-0.01em]"
              style={{
                color:
                  isCreditedInvoice ||
                  resolvedDisplayState === CmpInvoiceDisplayStateEnum.Voided
                    ? invoiceRed
                    : "#101827",
              }}
            >
              {getInvoiceDocumentTitle({
                displayState: resolvedDisplayState,
                documentMode,
              })}
            </div>
            <dl className="mt-3.5 flex w-full flex-col gap-1.5 text-[14px]">
              {[
                ["Invoice #:", row.invoiceNumber],
                [
                  "Invoice Date:",
                  (draftDocument?.invoiceDate
                    ? formatDraftCalendarDate(draftDocument.invoiceDate)
                    : null) ||
                    formatDate(
                      detail?.invoice.issuedAtUtcMilli || row.issuedAtUtcMilli,
                    ),
                ],
                [
                  "Due Date:",
                  (draftDocument?.dueDate
                    ? formatDraftCalendarDate(draftDocument.dueDate)
                    : null) ||
                    formatDate(
                      detail?.invoice.dueAtUtcMilli || row.dueAtUtcMilli,
                    ),
                ],
                ...(referencePurchaseOrder
                  ? [["Reference / PO:", referencePurchaseOrder]]
                  : []),
                // Design (21) CLAUDE.md "Billing method on non-membership
                // invoices": membership-related invoices (application,
                // renewal, add-on, Chamber Group Insurance) show their plan;
                // every other invoice shows One-Time (matches the backend PDF).
                [
                  "Billing Method:",
                  isGroupInsuranceInvoice
                    ? "Chamber Group Insurance"
                    : paymentPlan?.enabled
                      ? "Monthly Payment"
                      : isMembershipInvoice || isAddOnInvoice
                        ? "Annual Payment"
                        : "One-Time",
                ],
              ].map(([label, value]) => (
                <div className="flex justify-end gap-5" key={label}>
                  <dt>{label}</dt>
                  <dd className="min-w-[130px] text-right font-bold">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-[22px] flex items-center gap-6 rounded-lg border border-[#C7DCFF] bg-[#F4F8FF] px-5 py-3 text-[14px]">
              <span className="text-[#4B5563]">Status:</span>
              <span className="text-[14.5px] font-bold text-[#0091FF]">
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-[30px] mt-[34px] border-t border-[#E5E7EB]" />

        <div
          className="grid min-w-0 gap-[30px]"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}
        >
          <SnapshotBlock
            fallbackName={row.customerName}
            label="Sold To:"
            snapshot={soldToSnapshot}
          />
          <SnapshotBlock
            fallbackName={row.customerName}
            label="Bill To:"
            snapshot={billToSnapshot}
          />
        </div>

        <div className="mt-[38px] overflow-hidden">
          <div
            className={`grid ${lineItemGridClassName} bg-[#0091FF] px-3 py-[15px] text-[12px] font-semibold uppercase tracking-[0.06em] text-white`}
          >
            <div>Code</div>
            <div>Description</div>
            <div className="text-center">Qty</div>
            <div className="text-right">Unit Price</div>
            {showDiscountColumn ? (
              <div className="text-right">Discount</div>
            ) : null}
            <div className="text-right">Total</div>
          </div>
          {(lineItems.length ? lineItems : []).map((item) => (
            <div
              className={`grid ${lineItemGridClassName} items-center border-b border-[#E5E7EB] px-3 py-4 text-[14px] text-[#111827]`}
              key={item.id}
            >
              <div className="min-w-0 break-words font-bold">
                {getInvoicePaperLineItemCode(item) || "-"}
              </div>
              {/* Design (15) ds/InvoicePreviewModal.jsx:302: the line note sits
                  under the description, 13 px, muted. */}
              <div className="flex min-w-0 flex-col gap-[3px] break-words">
                <span>{item.description}</span>
                {item.note ? <span className="text-[13px] text-[#4B5563]">{item.note}</span> : null}
              </div>
              <div className="text-center">{item.quantity}</div>
              <div className="text-right">
                {formatDirectedCreditCurrency(
                  item.unitPriceInCents,
                  isCreditDocument,
                )}
              </div>
              {showDiscountColumn ? (
                <div className="text-right">
                  {"discountPercentage" in item &&
                  item.discountPercentage != null
                    ? `${item.discountPercentage.toFixed(1)}%`
                    : "—"}
                </div>
              ) : null}
              <div className="text-right font-semibold">
                {formatDirectedCreditCurrency(
                  getInvoicePaperLineItemTotal(item),
                  isCreditDocument,
                )}
              </div>
            </div>
          ))}
          {!lineItems.length ? (
            <div className="border-b border-gray-100 px-3 py-4 text-[14px] text-gray-500">
              No line items available for this invoice.
            </div>
          ) : null}
          <div
            aria-hidden="true"
            className="h-[104px] border-b border-gray-100"
            data-invoice-print-hide
          />
        </div>

        <div
          className="mt-[30px] grid gap-[30px]"
          data-invoice-print-keep
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
        >
          {/*
            Spec §4: coverage-period billing details beside the totals,
            mirroring the backend PDF's block verbatim. Membership invoices
            only render it when the coverage fields are present (legacy
            invoices predate them); the add-on invoice always carries its
            separate-billing sentence; other kinds have no details block.
          */}
          <div className="min-w-0 break-words text-[12.5px] leading-[1.5]">
            {isCreditDocument ? (
              <>
                <div className="text-[13.5px] font-bold">Credit Details</div>
                <p className="mt-3">
                  {formatCreditReason(creditInvoiceFields?.creditReason)}
                  {creditInvoiceFields?.creditReasonExplanation
                    ? `: ${creditInvoiceFields.creditReasonExplanation}`
                    : ""}
                </p>
                {creditInvoiceFields?.creditForInvoiceNumber ? (
                  <p className="mt-2">
                    Credit for invoice:{" "}
                    <span className="font-extrabold">
                      {creditInvoiceFields.creditForInvoiceNumber}
                    </span>
                  </p>
                ) : null}
                {creditInvoiceFields?.creditRefundMethodLabel ? (
                  <p className="mt-2">
                    Refund tracked via{" "}
                    <span className="font-extrabold">
                      {creditInvoiceFields.creditRefundMethodLabel}
                    </span>
                    {creditInvoiceFields.creditRefundedAtUtcMilli
                      ? ` on ${formatDate(
                          creditInvoiceFields.creditRefundedAtUtcMilli,
                        )}`
                      : ""}
                    {creditInvoiceFields.creditRefundReference
                      ? `. Reference: ${creditInvoiceFields.creditRefundReference}`
                      : ""}
                  </p>
                ) : null}
              </>
            ) : isEventInvoice && eventName ? (
              <>
                <div className="text-[13.5px] font-bold">Event Details</div>
                <p className="mt-3">
                  This invoice is for{" "}
                  <span className="font-extrabold">
                    {eventCode && !eventNameStartsWithCode(eventName, eventCode)
                      ? `${eventCode} - ${eventName}`
                      : eventName}
                  </span>
                  {hasEventDate ? (
                    <>
                      {" "}
                      on <span className="font-extrabold">{eventDate}</span>.
                    </>
                  ) : (
                    <>
                      .{" "}
                      <span className="font-extrabold">
                        Event date unavailable
                      </span>
                      .
                    </>
                  )}
                </p>
              </>
            ) : isAddOnInvoice ? (
              <>
                <div className="text-[13.5px] font-bold">
                  Additional Benefits & Services Billing Details
                </div>
                {hasCoveragePeriod && (
                  <p className="mt-3">
                    Your Additional Benefit is valid from:
                    <br />
                    <span className="font-extrabold">{coveragePeriodText}</span>
                  </p>
                )}
                <p className={hasCoveragePeriod ? "mt-2" : "mt-3"}>
                  This invoice covers the additional membership benefits you
                  selected during registration. These charges are invoiced
                  separately from your chamber membership fees and are payable
                  in full.
                </p>
              </>
            ) : isGroupInsuranceInvoice ? (
              <>
                <div className="text-[13.5px] font-bold">
                  Chamber Insurance Monthly Billing Enabled
                </div>
                <p className="mt-2">
                  Chamber Group Insurance Member Monthly Payment Option
                </p>
              </>
            ) : isMembershipInvoice && hasCoveragePeriod ? (
              <>
                {/* Claude Design buildInvoiceRecord membershipNote. */}
                <div className="text-[13.5px] font-bold">
                  {isPaidMembershipDocument
                    ? "Membership And Billing Method Details"
                    : "Membership And Payment Details"}
                </div>
                <p className="mt-2">Your Chamber Membership is valid from:</p>
                <p className="mt-2 font-bold">{coveragePeriodText}</p>
                {paymentPlan?.enabled ? (
                  <p className="mt-2">
                    You have opted to be billed monthly for your annual chamber
                    membership.
                  </p>
                ) : null}
                {/* Claude Design shows the monthly receipt copy on every
                  unpaid membership invoice; it is only true for a monthly
                  plan (Ajax invoice 128, Oct 14 2026, Moeen approved). */}
                <p className="mt-2">
                  {isPaidMembershipDocument || !paymentPlan?.enabled
                    ? "You will automatically receive payment receipt, when invoice is paid."
                    : "You will automatically receive monthly payment receipt."}
                </p>
              </>
            ) : null}
          </div>

          <div className="min-w-0 space-y-2 break-words text-[14.5px]">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-bold">
                {formatDirectedCreditCurrency(subtotal, isCreditDocument)}
              </span>
            </div>
            <div className="flex flex-col gap-1 border-b border-gray-200 pb-3">
              <div className="flex justify-between">
                <span>{taxLabel}</span>
                <span className="font-bold">
                  {formatDirectedCreditCurrency(tax, isCreditDocument)}
                </span>
              </div>
              {/* Design (15) ds/InvoicePreviewModal.jsx:343. */}
              {taxNote ? <div className="text-right text-[12px] leading-[1.45] text-[#4B5563]">{taxNote}</div> : null}
            </div>
            <div className="flex items-end justify-between gap-4 pt-2 text-[19px] font-bold">
              <span>
                {documentMode === "PAYMENT_RECEIPT"
                  ? "TOTAL RECEIVED"
                  : isCreditDocument
                    ? "TOTAL CREDIT"
                    : "TOTAL DUE"}
              </span>
              <span className="text-[27px]">
                {formatDirectedCreditCurrency(
                  documentMode === "PAYMENT_RECEIPT"
                    ? receiptAmountInCents
                    : isCreditDocument
                      ? creditedAmountInCents
                      : total,
                  isCreditDocument && documentMode !== "PAYMENT_RECEIPT",
                )}
              </span>
            </div>
            {latestPayment && !isCreditDocument ? (
              <div className="flex justify-between border-t border-gray-200 pt-3 text-[12.5px] font-bold uppercase">
                <span>
                  Payment {formatDate(latestPayment.receivedAtUtcMilli)}
                </span>
                <span>{formatCurrency(latestPayment.amountInCents)}</span>
              </div>
            ) : null}
            {isCreditDocument ? (
              <div className="flex justify-between border-t border-gray-200 pt-3 text-[12.5px] font-bold uppercase">
                <span>
                  {formatCreditReason(creditInvoiceFields?.creditReason)}
                </span>
                <span>
                  {formatDirectedCreditCurrency(creditedAmountInCents, true)}
                </span>
              </div>
            ) : isCreditedInvoice && latestCredit ? (
              <div className="flex justify-between border-t border-gray-200 pt-3 text-[12.5px] font-bold uppercase">
                <span>
                  Credit{" "}
                  {formatDate(
                    latestCredit.completedAtUtcMilli ||
                      latestCredit.createdAtUtcMilli,
                  )}
                </span>
                <span>-{formatCurrency(latestCredit.refundAmountInCents)}</span>
              </div>
            ) : resolvedDisplayState === CmpInvoiceDisplayStateEnum.Voided ? (
              <div className="flex justify-between border-t border-gray-200 pt-3 text-[12.5px] font-bold uppercase">
                <span>Voided Amount</span>
                <span>{formatCurrency(total)}</span>
              </div>
            ) : null}
            {paymentPlan?.enabled ||
            isPaidInvoice ||
            isCreditedInvoice ||
            // A partly paid invoice shows what is still owing under the
            // payment line (Ajax invoice 128, Oct 14 2026).
            (!!latestPayment && !isCreditDocument && amountDue !== total) ||
            resolvedDisplayState === CmpInvoiceDisplayStateEnum.Voided ||
            documentMode === "PAYMENT_RECEIPT" ? (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-end justify-between gap-4 text-[19px] font-bold">
                  <span>
                    {documentMode === "PAYMENT_RECEIPT"
                      ? "BALANCE"
                      : "BALANCE DUE"}
                  </span>
                  <span className="text-[27px]">
                    {formatCurrency(amountDue)}
                  </span>
                </div>
                {paymentPlan?.enabled ? (
                  <div className="mt-2 border-t border-gray-200 pt-2 text-[12px] font-bold uppercase">
                    {Math.max(
                      0,
                      paymentPlan.totalInstallments -
                        paymentPlan.paidInstallments,
                    )}{" "}
                    instalments remaining
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {paymentBoxKind !== "none" ? (
          <div
            className="mt-[34px] rounded-[10px] border border-[#0091FF] px-[30px] py-[26px]"
            data-invoice-print-keep
          >
            {paymentBoxKind === "received" ? (
              <div className="text-[14px] font-bold uppercase tracking-[0.08em]">
                Payment received via{" "}
                {latestPayment?.paymentMethodLabel ||
                  getRecordedPaymentMethodLabel(row.paymentMethodLabel) ||
                  "payment"}
              </div>
            ) : paymentBoxKind === "insurance" ? (
              <div className="text-[14px] font-bold uppercase tracking-[0.08em]">
                Payment options via direct deposit
              </div>
            ) : (
              <>
                <div className="mb-[22px] text-[14px] font-bold uppercase tracking-[0.08em]">
                  Payment Options
                </div>
                <div
                  className="grid min-w-0 gap-0"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  {paymentCards.map((card) => (
                    <div
                      className="flex min-w-0 flex-col gap-2 break-words border-l border-[#0091FF] px-[18px] first:border-l-0 first:pl-0"
                      key={card.label}
                    >
                      <div className="text-[14.5px] font-bold">{card.label}</div>
                      {card.lines.map((line, lineIndex) => (
                        <div
                          className={`leading-[1.45] ${
                            line.small ? "text-[11px]" : "text-[11.5px]"
                          } ${line.bold ? "font-bold" : "font-normal"}`}
                          key={`${card.label}-${lineIndex}`}
                        >
                          {line.text}
                        </div>
                      ))}
                      {card.cta ? (
                        <Button
                          disabled={isDraftDocument || !canPayNow || !onPayNow}
                          onClick={onPayNow}
                          sx={{
                            alignSelf: "flex-start",
                            backgroundColor: invoiceBlue,
                            borderRadius: "8px",
                            color: "white",
                            fontSize: 14,
                            fontWeight: 700,
                            mt: 0.75,
                            minHeight: 42,
                            px: 2.75,
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#0B74D5" },
                            "&.Mui-disabled": {
                              backgroundColor: "#9E9E9E",
                              color: "white",
                            },
                          }}
                        >
                          {card.cta}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        <div
          className="mt-9 grid min-w-0 items-start gap-[30px]"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
        >
          <div className="min-w-0 break-words text-[12.5px] leading-[1.5]">
            <div className="text-[13.5px] font-bold">Payment Terms</div>
            <p className="mt-3">
              Thank you for your support of the {chamber?.name || "chamber"}.
            </p>
            {/*
              Payment-terms note comes from the invoice's publicMemo (which the
              backend seeds from Chamber Setup's paymentTermsNote at creation).
              The create-only defaults query (paymentTermsNote) is not threaded
              into this modal, so we render the invoice value when present and
              nothing otherwise rather than showing a placeholder token.
            */}
            {draftDocument?.publicMemo || detail?.invoice.publicMemo ? (
              <p className="mt-3">
                {draftDocument?.publicMemo || detail?.invoice.publicMemo}
              </p>
            ) : null}
            {creditedByLines.map((line) => (
              <p className="mt-3" key={line}>
                {line}
              </p>
            ))}
          </div>
          <div className="flex min-w-0 flex-col gap-2.5 break-words rounded-[10px] border border-gray-200 px-[22px] py-5 text-[13.5px] leading-[1.5]">
            {/* Design typo "inquires" corrected to "inquiries". */}
            <div className="font-bold">Billing inquiries contact</div>
            {presentation ? (
              <InvoiceBillingInquiryContact presentation={presentation} chamberPhone={chamber?.phoneNumber} />
            ) : chamber?.phoneNumber ? (
              <div className="flex items-center gap-[9px]">
                <InvoicePhoneIcon />
                {chamber.phoneNumber}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-[34px] border-t border-gray-100 pt-5 text-center text-[15.5px]">
          {chamber?.name || "Chamber of Commerce"}
          {taxRegistrationNumber ? (
            <>
              {" "}
              <strong>HST#</strong> {taxRegistrationNumber}
            </>
          ) : null}
        </div>
        <div className="mt-4 border-t border-gray-100 pt-[18px] text-center text-[12.5px]">
          Thank you for being a valued member of the{" "}
          {chamber?.name || "chamber"}.
        </div>
      </div>
    </div>
  );
};

export const InvoicePreviewModal = ({
  actionLoading,
  canApplyPayment,
  canEmailDocument = false,
  canPrintPdf,
  canSendInvoice,
  canSendReceipt = false,
  chamber,
  detail,
  documentMode = "INVOICE",
  draftDocument,
  errorMessage,
  loading,
  onAddPayment,
  onClose,
  onCreateInvoice,
  onEmailDocument,
  onPendingAction,
  onPrintPdf,
  onSaveDraft,
  onSendInvoice,
  onSendReceipt,
  open,
  pendingAction,
  row,
  sendIsResend,
}: {
  actionLoading: boolean;
  canApplyPayment: boolean;
  canEmailDocument?: boolean;
  canPrintPdf: boolean;
  canSendInvoice: boolean;
  canSendReceipt?: boolean;
  chamber?: ChamberInfo;
  detail?: InvoiceDetail | null;
  documentMode?: InvoiceDocumentMode;
  draftDocument?: InvoiceDraftDocumentData | null;
  errorMessage?: string | null;
  loading: boolean;
  onAddPayment: () => void;
  onClose: () => void;
  onCreateInvoice?: () => void;
  onEmailDocument?: () => void;
  onPendingAction?: () => void;
  onPrintPdf: () => void;
  // Draft previews opened from a Create or Update page pass their save
  // handler. A draft opened from a Manage Invoices row has no Save Draft.
  onSaveDraft?: () => void;
  onSendInvoice: () => void;
  onSendReceipt: () => void;
  open: boolean;
  pendingAction?: InvoicePreviewPendingAction;
  row: InvoiceDocumentRow | null;
  sendIsResend: boolean;
}) => {
  const [zoom, setZoom] = useState<25 | 50 | 75 | 100 | "fit">("fit");
  const previewViewportRef = useRef<HTMLDivElement | null>(null);
  const previewPageRef = useRef<HTMLDivElement | null>(null);
  const handlers: Record<InvoicePreviewAction["key"], () => void> = {
    printPdf: onPrintPdf,
    addPayment: onAddPayment,
    createInvoice: onCreateInvoice || (() => undefined),
    saveDraft: onSaveDraft || (() => undefined),
    pendingLifecycleAction: onPendingAction || (() => undefined),
    emailDocument: onEmailDocument || (() => undefined),
    sendInvoice: onSendInvoice,
    sendReceipt: onSendReceipt,
  };

  // Only trust `detail` once it belongs to the current row and has the same
  // display state. Apollo can briefly retain an older detail after a mutation,
  // including NotPaid detail after the row becomes Voided.
  const detailForRow =
    row &&
    detail?.invoice.id === row.id &&
    detail.displayState === row.displayState
      ? detail
      : null;
  const payerDataWarning =
    detailForRow?.payerDataWarning || row?.payerDataWarning || null;
  const downloadPdfAvailability = (
    detailForRow?.availableActions ||
    row?.availableActions ||
    []
  ).find((action) => action.action === CmpInvoiceActionTypeEnum.DownloadPdf);
  const customerPayerDataUnavailableReason =
    downloadPdfAvailability && !downloadPdfAvailability.available
      ? downloadPdfAvailability.reason
      : null;
  const actions = row
    ? getInvoicePreviewActions({
        displayState: detailForRow?.displayState || row.displayState,
        documentMode,
        hasPayments: (detailForRow?.payments?.length || 0) > 0,
        ruleKind:
          detailForRow?.invoice.paymentRules?.ruleKind ||
          row.paymentRules?.ruleKind,
        canApplyPayment,
        canEmailDocument: canEmailDocument && Boolean(onEmailDocument),
        canSaveDraft: Boolean(onSaveDraft),
        canSendInvoice,
        canSendReceipt,
        pendingAction,
        sendIsResend,
      })
    : [];
  const previewFitDependencies = useMemo(
    () => [detailForRow, documentMode, draftDocument, row],
    [detailForRow, documentMode, draftDocument, row],
  );
  const { fitScale, pageHeight } = useInvoicePreviewFitScale({
    open: open && Boolean(row),
    pageWidth: invoicePreviewPageWidth,
    viewportRef: previewViewportRef,
    pageRef: previewPageRef,
    deps: previewFitDependencies,
  });
  const scale = zoom === "fit" ? fitScale : zoom / 100;

  useEffect(() => {
    if (open) {
      setZoom("fit");
    }
  }, [documentMode, open, row?.id]);

  return (
    <Dialog
      fullScreen
      open={open}
      PaperProps={{ sx: { backgroundColor: "#EFF3F8" } }}
      onClose={onClose}
    >
      <InvoicePreviewPrintStyles />
      <div
        className="min-h-screen bg-[#EFF3F8] px-4 py-8 sm:px-7"
        style={{ fontFamily: invoiceDesignFontFamily }}
      >
        <div className="mx-auto mb-4 flex max-w-[1070px] flex-wrap items-center justify-between gap-3">
          <InvoicePreviewZoomBar zoom={zoom} onZoomChange={setZoom} />
          <IconButton
            aria-label="Close invoice preview"
            onClick={onClose}
            sx={{
              backgroundColor: invoiceRed,
              color: "white",
              height: invoicePreviewCloseButtonSize,
              width: invoicePreviewCloseButtonSize,
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className="mx-auto mb-10 flex max-w-[1070px] flex-wrap items-start justify-end gap-5 pr-8">
          {actions.map((action) => {
            const isPrint = action.key === "printPdf";
            // Claude Design: the "Email" button carries the send glyph.
            const isEmail =
              action.label === "Email" &&
              (action.key === "sendInvoice" ||
                action.key === "sendReceipt" ||
                action.key === "emailDocument");
            const button = (
              <InvoiceActionButton
                color={action.color}
                disabled={
                  actionLoading ||
                  (isPrint && !canPrintPdf) ||
                  (action.key === "createInvoice" && !onCreateInvoice) ||
                  (action.key === "saveDraft" && !onSaveDraft) ||
                  (action.key === "pendingLifecycleAction" && !onPendingAction)
                }
                icon={
                  isPrint ? (
                    <LocalPrintshopOutlinedIcon />
                  ) : isEmail ? (
                    <InvoiceSendIcon />
                  ) : undefined
                }
                onClick={handlers[action.key]}
              >
                {action.label}
              </InvoiceActionButton>
            );

            if (!action.caption && !action.withEmailPreview) {
              return <React.Fragment key={action.key}>{button}</React.Fragment>;
            }

            return (
              <div
                key={action.key}
                className="flex max-w-[240px] flex-col items-end gap-1.5"
              >
                {button}
                {action.caption ? (
                  <p className="max-w-[210px] text-right text-[11.5px] leading-[1.4] text-[#111827]">
                    {action.caption}
                  </p>
                ) : null}
                {action.withEmailPreview ? (
                  <div className="mt-2 flex flex-col items-end">
                    <span className="inline-flex items-center gap-[7px] text-[15px] font-bold text-[#111827]">
                      <VisibilityOutlinedIcon sx={{ fontSize: 15 }} /> Preview
                      Email
                    </span>
                    <div className="text-[11.5px] text-[#111827]">
                      {action.previewSubline || "To be sent with invoice"}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {errorMessage ? (
          <Alert className="mx-auto mb-6 max-w-[1070px]" severity="error">
            {errorMessage}
          </Alert>
        ) : null}
        {payerDataWarning ? (
          <Alert className="mx-auto mb-6 max-w-[1070px]" severity="warning">
            {payerDataWarning}
          </Alert>
        ) : null}
        {customerPayerDataUnavailableReason ? (
          <Alert className="mx-auto mb-6 max-w-[1070px]" severity="warning">
            You cannot print or email this invoice right now.{" "}
            {customerPayerDataUnavailableReason}
          </Alert>
        ) : null}
        {loading && !detailForRow ? (
          <div className="mx-auto flex max-w-[1070px] items-center justify-center gap-3 rounded bg-white p-10 text-sm font-semibold text-gray-500">
            <CircularProgress size={20} />
            Loading invoice preview...
          </div>
        ) : row ? (
          <div
            className="mx-auto w-full max-w-[1070px] overflow-x-auto overflow-y-hidden"
            ref={previewViewportRef}
          >
            <div
              className="mx-auto"
              style={{
                height: pageHeight ? pageHeight * scale : undefined,
                width: invoicePreviewPageWidth * scale,
              }}
            >
              <div
                data-invoice-preview-page
                ref={previewPageRef}
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: invoicePreviewPageWidth,
                }}
              >
                <InvoicePaper
                  canPayNow={canApplyPayment}
                  chamber={chamber}
                  detail={detailForRow}
                  documentMode={documentMode}
                  draftDocument={draftDocument}
                  onPayNow={onAddPayment}
                  row={row}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export const UnsavedInvoicePreviewModal = ({
  actionLoading = false,
  chamber,
  draftDocument,
  errorMessage,
  onClose,
  onCreateInvoice,
  onSaveDraft,
  open,
}: {
  actionLoading?: boolean;
  chamber?: ChamberInfo;
  draftDocument: InvoiceDraftDocumentData;
  /**
   * A save or create error that the page reports (UX audit INV-3/INV-4).
   * The full-screen preview covers the page, so it shows the error too.
   */
  errorMessage?: string | null;
  onClose: () => void;
  onCreateInvoice: () => Promise<void> | void;
  /**
   * Retained for existing callers only. The Claude Design draft toolbar has
   * no print action (Save Draft + Approve/Create Invoice), so it is unused.
   */
  onPrintPdf?: () => Promise<void> | void;
  onSaveDraft?: () => Promise<void> | void;
  open: boolean;
}): React.ReactElement => {
  const [localActionLoading, setLocalActionLoading] = useState(false);
  const [localActionError, setLocalActionError] = useState<string | null>(null);
  const actionInFlightRef = useRef(false);
  const row: InvoiceDocumentRow = {
    customerName: draftDocument.customerName,
    displayState: CmpInvoiceDisplayStateEnum.Draft,
    id: draftDocument.id || "unsaved-invoice-draft",
    invoiceKind: draftDocument.invoiceKind,
    invoiceNumber: draftDocument.invoiceNumber || "Draft",
    issuedAtUtcMilli: 0,
    balanceDueInCents: draftDocument.totalInCents,
    totalAmountInCents: draftDocument.totalInCents,
  };

  useEffect(() => {
    if (open && !actionInFlightRef.current) {
      setLocalActionLoading(false);
      setLocalActionError(null);
    }
  }, [draftDocument.id, open]);

  const runPreviewAction = async (action: () => Promise<void> | void) => {
    if (actionLoading || actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setLocalActionLoading(true);
    setLocalActionError(null);
    try {
      await action();
    } catch (error) {
      setLocalActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("finish this invoice action"),
        ),
      );
    } finally {
      actionInFlightRef.current = false;
      setLocalActionLoading(false);
    }
  };

  return (
    <InvoicePreviewModal
      actionLoading={actionLoading || localActionLoading}
      canApplyPayment={false}
      canPrintPdf
      canSendInvoice={false}
      chamber={chamber}
      draftDocument={draftDocument}
      errorMessage={localActionError || errorMessage || null}
      loading={false}
      onAddPayment={() => undefined}
      onClose={onClose}
      onCreateInvoice={() => void runPreviewAction(onCreateInvoice)}
      onPrintPdf={() => undefined}
      onSaveDraft={
        onSaveDraft ? () => void runPreviewAction(onSaveDraft) : undefined
      }
      onSendInvoice={() => undefined}
      onSendReceipt={() => undefined}
      open={open}
      row={row}
      sendIsResend={false}
    />
  );
};

export const processPaymentDialogMaxWidth = 700;
export const processPaymentFieldSx = invoiceProcessPaymentFieldSx;

// UX audit INV-8 timings for opening the secure card form.
export const cardCheckoutFirstStartDelayUtcMilli = 250;
export const cardCheckoutAmountEditDelayUtcMilli = 900;

// UX audit INV-8: a blank, zero or too-large card amount opens no Stripe
// Session, so say what to fix instead of a spinner that never ends.
export const getCardPaymentAmountError = ({
  amountInCents,
  eligibleCardAmountInCents,
}: {
  amountInCents: number;
  eligibleCardAmountInCents: number;
}): string | null =>
  amountInCents > 0 && amountInCents <= eligibleCardAmountInCents
    ? null
    : `Enter an amount up to ${formatCurrency(
        eligibleCardAmountInCents,
      )} in Amount to Pay. The secure card form opens when the amount is valid.`;

export const isProcessPaymentModalProcessing = ({
  recordingManualPayment,
  startingCardCheckout,
}: {
  recordingManualPayment: boolean;
  startingCardCheckout: boolean;
}): boolean => recordingManualPayment || startingCardCheckout;

export type CardCheckoutPaymentReadbackState =
  | "NONE"
  | "APPLIED"
  | "RECONCILIATION_REQUIRED";

const terminalCardCheckoutEventTypes = new Set<CmpOperationalEventTypeEnum>([
  CmpOperationalEventTypeEnum.InvoicePaymentReceivedAfterSettlement,
  CmpOperationalEventTypeEnum.InvoicePaymentReceivedAfterVoid,
  CmpOperationalEventTypeEnum.InvoicePaymentReceivedFromRetiredSurface,
]);

export const getCardCheckoutPaymentReadbackState = ({
  detail,
  handoff,
  invoiceId,
}: {
  detail?: InvoiceDetail | null;
  handoff?: SecureCardCheckoutHandoffData | null;
  invoiceId?: string | null;
}): CardCheckoutPaymentReadbackState => {
  if (!detail || !handoff || !invoiceId || detail.invoice.id !== invoiceId) {
    return "NONE";
  }

  const sessionPayments = detail.payments.filter(
    (payment) =>
      payment.stripeCheckoutSessionId === handoff.checkoutSessionId,
  );

  if (!sessionPayments.length) {
    return "NONE";
  }

  const matchingPayments = sessionPayments.filter(
    (payment) =>
      payment.invoiceId === invoiceId &&
      payment.amountInCents === handoff.amountInCents,
  );

  if (matchingPayments.length !== sessionPayments.length) {
    return "RECONCILIATION_REQUIRED";
  }

  const matchingPaymentIds = new Set(
    matchingPayments.map((payment) => payment.id),
  );
  const matchingPaymentEvents = detail.operationalEvents.filter(
    (event) =>
      event.invoiceId === invoiceId &&
      Boolean(event.paymentId && matchingPaymentIds.has(event.paymentId)),
  );
  const hasTerminalCapture = matchingPaymentEvents.some((event) =>
    terminalCardCheckoutEventTypes.has(event.eventType),
  );

  if (hasTerminalCapture || matchingPayments.length !== 1) {
    return "RECONCILIATION_REQUIRED";
  }

  const [matchingPayment] = matchingPayments;
  const paymentHasAppliedStatus =
    matchingPayment.status === CmpPaymentStatusEnum.Paid ||
    matchingPayment.status === CmpPaymentStatusEnum.PartiallyRefunded;
  const paymentWasApplied =
    paymentHasAppliedStatus &&
    matchingPaymentEvents.some(
      (event) =>
        event.paymentId === matchingPayment.id &&
        event.eventType === CmpOperationalEventTypeEnum.StripePaymentSucceeded,
    );

  // Generic Stripe payment records exist only after Stripe reports a captured
  // payment. Any incomplete or conflicting audit pair must lock the form so a
  // staff user cannot submit the same Session again.
  return paymentWasApplied ? "APPLIED" : "RECONCILIATION_REQUIRED";
};

export const ProcessPaymentModal = ({
  actionError,
  cardCheckoutRestriction,
  detail,
  loading,
  onClose,
  onCardPaymentSubmitted,
  onOpenEventSales,
  onEmailReceipt,
  onPrintReceipt,
  onRecordManualPayment,
  onRefreshCardCheckoutStatus,
  onRequestHostedFallback,
  onSaveCardCheckoutNote,
  onStartCardCheckout,
  open,
  recordingManualPayment,
  receiptActionLoading = false,
  requireManualPaymentConfirmation = true,
  row,
  cardCheckoutReturnUrl,
  staffRecordablePaymentMethods,
  startingCardCheckout,
}: {
  actionError?: string | null;
  cardCheckoutRestriction?: CmpEventCardCheckoutRestriction | null;
  cardCheckoutReturnUrl: string;
  detail?: InvoiceDetail | null;
  loading: boolean;
  onClose: () => void;
  onCardPaymentSubmitted: () => Promise<void> | void;
  onOpenEventSales: (
    destination: CmpEventCardCheckoutRestriction["destination"],
  ) => void;
  onEmailReceipt?: () => Promise<void> | void;
  onPrintReceipt?: () => Promise<void> | void;
  onRecordManualPayment: (input: RecordInvoicePaymentInput) => Promise<boolean>;
  onRefreshCardCheckoutStatus: () => Promise<void>;
  onRequestHostedFallback: (
    customCheckoutSessionId: string,
  ) => Promise<SecureCardCheckoutHandoffData | null>;
  /** Saves Internal Notes on the open card checkout; throws to stop submit. */
  onSaveCardCheckoutNote: SaveCardCheckoutNote;
  onStartCardCheckout: (
    amountInCents: number,
  ) => Promise<SecureCardCheckoutHandoffData | null>;
  open: boolean;
  recordingManualPayment: boolean;
  receiptActionLoading?: boolean;
  requireManualPaymentConfirmation?: boolean;
  row: InvoiceRow | null;
  /** The chamber's staff-recordable methods (cmpStaffRecordablePaymentMethods). */
  staffRecordablePaymentMethods: readonly StaffRecordablePaymentMethod[];
  startingCardCheckout: boolean;
}) => {
  const eventPaymentRestricted = Boolean(cardCheckoutRestriction);
  const detailForRow = row && detail?.invoice.id === row.id ? detail : null;
  const payerDataWarning =
    detailForRow?.payerDataWarning || row?.payerDataWarning || null;
  const paymentCapabilities = getInvoicePaymentCapabilities({
    availableActions:
      detailForRow?.availableActions || row?.availableActions || [],
  });
  const rules = getPaymentRules(detailForRow, row);
  const cardCheckoutUnavailableReason = !paymentCapabilities.cardCheckout
    .available
    ? paymentCapabilities.cardCheckout.reason
    : null;
  const paymentUnavailableReason =
    paymentCapabilities.manualPayment.reason ||
    paymentCapabilities.cardCheckout.reason;
  // Credit Card stays as offered today. Manual methods are what staff can
  // record as received, from the shared staff payment method helper; the
  // backend's Record Manual Payment action still gates them.
  const methodOptions = useMemo((): PaymentMethodOption[] => {
    if (eventPaymentRestricted) {
      return [];
    }

    return [
      ...(rules?.creditCardAllowed && paymentCapabilities.cardCheckout.available
        ? [
            {
              displayLabel: creditCardPaymentMethodLabel,
              isCreditCard: true,
              mutationLabel: creditCardPaymentMethodLabel,
            },
          ]
        : []),
      ...(paymentCapabilities.manualPayment.available
        ? getStaffPaymentMethodOptions({
            paymentRules: rules,
            recordableMethods: staffRecordablePaymentMethods,
          }).map((method) => ({
            displayLabel: method.label,
            isCreditCard: false,
            mutationLabel: method.recordLabel,
          }))
        : []),
    ];
  }, [
    eventPaymentRestricted,
    paymentCapabilities.cardCheckout.available,
    paymentCapabilities.manualPayment.available,
    rules,
    staffRecordablePaymentMethods,
  ]);
  const [selectedMethodLabel, setSelectedMethodLabel] = useState("");
  const [paymentDate, setPaymentDate] = useState(formatDateInput(Date.now()));
  const [amountInput, setAmountInput] = useState("0.00");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const saveCardNoteBeforeConfirm = useCardCheckoutNoteBeforeConfirm({
    note,
    saveNote: onSaveCardCheckoutNote,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [localPaymentError, setLocalPaymentError] = useState<string | null>(
    null,
  );
  const [cardCheckoutHandoff, setCardCheckoutHandoff] =
    useState<SecureCardCheckoutHandoffData | null>(null);
  const [cardCheckoutHandoffFingerprint, setCardCheckoutHandoffFingerprint] =
    useState("");
  const [cardCheckoutStartError, setCardCheckoutStartError] = useState<
    string | null
  >(null);
  const [cardCheckoutRetryNonce, setCardCheckoutRetryNonce] = useState(0);
  const [capturedCardCheckoutSessionId, setCapturedCardCheckoutSessionId] =
    useState("");
  // UX audit INV-1: true from card submit until the invoice shows the payment
  // or Stripe returns an error. Close, amount and method stay locked so staff
  // cannot open a second card checkout before the Stripe webhook lands.
  const [cardPaymentInProgress, setCardPaymentInProgress] = useState(false);
  const activeCardFingerprintRef = useRef("");
  const cardCheckoutRequestedThisOpenRef = useRef(false);
  const lastRequestedCardFingerprintRef = useRef("");
  const onStartCardCheckoutRef = useRef(onStartCardCheckout);
  const initialPaymentAmountRef = useRef(0);
  const manualPaymentInFlightRef = useRef(false);

  const amountInCents = parseCurrencyInput(amountInput);
  const paymentPlan = getPaymentPlan(detailForRow, row);
  const outstandingBalance = getAmountDue(detailForRow, row);
  const eligibleCardAmountInCents =
    paymentPlan?.nextInstallmentAmountInCents || outstandingBalance;
  initialPaymentAmountRef.current = getNextPaymentAmount(detailForRow, row);
  const selectedMethod =
    methodOptions.find(
      (method) => method.mutationLabel === selectedMethodLabel,
    ) || null;
  const cardPaymentWasSelected =
    selectedMethodLabel === creditCardPaymentMethodLabel;
  const isCreditCardSelected = Boolean(selectedMethod?.isCreditCard);
  const processing = isProcessPaymentModalProcessing({
    recordingManualPayment,
    startingCardCheckout,
  });
  const dialogLocked = processing || cardPaymentInProgress;
  const receiptBillingSnapshot = getBillToSnapshot(detailForRow);
  const cardCheckoutSelectionFingerprint = row
    ? `${row.id}:${amountInCents}`
    : "";
  const activeCardCheckoutHandoff =
    cardPaymentWasSelected &&
    cardCheckoutHandoffFingerprint === cardCheckoutSelectionFingerprint
      ? cardCheckoutHandoff
      : null;
  const cardPaymentExperienceActive = Boolean(
    isCreditCardSelected || activeCardCheckoutHandoff,
  );
  const currentCardCheckoutPaymentReadbackState =
    getCardCheckoutPaymentReadbackState({
      detail: detailForRow,
      handoff: activeCardCheckoutHandoff,
      invoiceId: row?.id,
    });
  const activeCardCheckoutSessionId =
    activeCardCheckoutHandoff?.checkoutSessionId || "";

  useEffect(() => {
    if (
      activeCardCheckoutSessionId &&
      currentCardCheckoutPaymentReadbackState !== "NONE"
    ) {
      setCapturedCardCheckoutSessionId(activeCardCheckoutSessionId);
    }
  }, [
    activeCardCheckoutSessionId,
    currentCardCheckoutPaymentReadbackState,
  ]);

  const cardCheckoutPaymentReadbackState =
    currentCardCheckoutPaymentReadbackState === "NONE" &&
    activeCardCheckoutSessionId &&
    capturedCardCheckoutSessionId === activeCardCheckoutSessionId
      ? "RECONCILIATION_REQUIRED"
      : currentCardCheckoutPaymentReadbackState;
  const cardCheckoutControlsLocked =
    cardCheckoutPaymentReadbackState !== "NONE" || cardPaymentInProgress;
  const effectiveCardCheckoutStatus: SecureCardCheckoutStatus =
    cardCheckoutPaymentReadbackState === "APPLIED"
      ? "PAID"
      : cardCheckoutPaymentReadbackState === "RECONCILIATION_REQUIRED"
        ? "RECONCILIATION_REQUIRED"
        : "PENDING";
  const cardAmountIsEligible =
    amountInCents > 0 && amountInCents <= eligibleCardAmountInCents;
  const cardAmountError = getCardPaymentAmountError({
    amountInCents,
    eligibleCardAmountInCents,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedMethodLabel("");
    setPaymentDate(formatDateInput(Date.now()));
    setAmountInput(formatCurrencyInput(initialPaymentAmountRef.current));
    setReference("");
    setNote("");
    setConfirmOpen(false);
    setSuccessOpen(false);
    setLocalPaymentError(null);
    setCardCheckoutStartError(null);
    setCardCheckoutRetryNonce(0);
    manualPaymentInFlightRef.current = false;
  }, [open, row?.id]);

  useEffect(() => {
    setCardCheckoutHandoff(null);
    setCardCheckoutHandoffFingerprint("");
    setCardCheckoutStartError(null);
    setCapturedCardCheckoutSessionId("");
    setCardPaymentInProgress(false);
    activeCardFingerprintRef.current = "";
    lastRequestedCardFingerprintRef.current = "";
    cardCheckoutRequestedThisOpenRef.current = false;
  }, [open, row?.id]);

  useEffect(() => {
    onStartCardCheckoutRef.current = onStartCardCheckout;
  }, [onStartCardCheckout]);

  useEffect(() => {
    if (
      !open ||
      eventPaymentRestricted ||
      !cardCheckoutSelectionFingerprint ||
      !cardPaymentWasSelected
    ) {
      setCardCheckoutHandoff(null);
      setCardCheckoutHandoffFingerprint("");
      setCardCheckoutStartError(null);
      activeCardFingerprintRef.current = "";
      lastRequestedCardFingerprintRef.current = "";
      return undefined;
    }

    // Authoritative payment readback can remove card capability, reduce the
    // balance, and advance an installment. Keep the exact active Session until
    // its saved payment and audit event are reconciled by the handoff.
    if (activeCardCheckoutHandoff) {
      return undefined;
    }

    if (!isCreditCardSelected || !cardAmountIsEligible) {
      setCardCheckoutHandoff(null);
      setCardCheckoutHandoffFingerprint("");
      setCardCheckoutStartError(null);
      activeCardFingerprintRef.current = "";
      lastRequestedCardFingerprintRef.current = "";
      return undefined;
    }

    const fingerprint = cardCheckoutSelectionFingerprint;

    if (lastRequestedCardFingerprintRef.current === fingerprint) {
      return undefined;
    }

    // UX audit INV-8: the first form opens quickly. Later amount edits wait
    // for a longer typing pause, so each keystroke does not open a new Stripe
    // Session.
    const startDelayUtcMilli = cardCheckoutRequestedThisOpenRef.current
      ? cardCheckoutAmountEditDelayUtcMilli
      : cardCheckoutFirstStartDelayUtcMilli;
    lastRequestedCardFingerprintRef.current = fingerprint;
    activeCardFingerprintRef.current = fingerprint;
    cardCheckoutRequestedThisOpenRef.current = true;
    setCardCheckoutHandoff(null);
    setCardCheckoutHandoffFingerprint("");
    setCardCheckoutStartError(null);
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      void onStartCardCheckoutRef
        .current(amountInCents)
        .then((handoff) => {
          if (cancelled || activeCardFingerprintRef.current !== fingerprint) {
            return;
          }

          if (!handoff || handoff.amountInCents !== amountInCents) {
            setCardCheckoutStartError(
              buildStaffErrorFallback("open the card form", "No payment was taken."),
            );
            return;
          }

          setLocalPaymentError(null);
          setCardCheckoutHandoff(handoff);
          setCardCheckoutHandoffFingerprint(fingerprint);
        })
        .catch((error: unknown) => {
          if (cancelled || activeCardFingerprintRef.current !== fingerprint) {
            return;
          }

          setCardCheckoutStartError(
            getUserFacingErrorText(
              error,
              buildStaffErrorFallback("open the card form", "No payment was taken."),
            ),
          );
        });
    }, startDelayUtcMilli);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    activeCardCheckoutHandoff,
    amountInCents,
    cardAmountIsEligible,
    cardCheckoutRetryNonce,
    cardCheckoutSelectionFingerprint,
    cardPaymentWasSelected,
    eventPaymentRestricted,
    isCreditCardSelected,
    open,
  ]);

  const handleRetryCardCheckout = () => {
    if (
      eventPaymentRestricted ||
      !isCreditCardSelected ||
      !cardAmountIsEligible ||
      !cardCheckoutSelectionFingerprint ||
      startingCardCheckout
    ) {
      return;
    }

    activeCardFingerprintRef.current = "";
    lastRequestedCardFingerprintRef.current = "";
    // A Retry click is not typing, so it opens the form without the long wait.
    cardCheckoutRequestedThisOpenRef.current = false;
    setCardCheckoutHandoff(null);
    setCardCheckoutHandoffFingerprint("");
    setCardCheckoutStartError(null);
    setCardCheckoutRetryNonce((value) => value + 1);
  };

  const handleConfirmPayment = async () => {
    if (
      eventPaymentRestricted ||
      manualPaymentInFlightRef.current ||
      !selectedMethod ||
      amountInCents <= 0 ||
      amountInCents > outstandingBalance
    ) {
      return;
    }

    if (isCreditCardSelected) {
      return;
    }

    if (!isCalendarDateValue(paymentDate)) {
      setLocalPaymentError("Pick a Payment Date from the calendar, then click Confirm & Record Payment again.");
      return;
    }

    setLocalPaymentError(null);
    manualPaymentInFlightRef.current = true;

    try {
      const succeeded = await onRecordManualPayment({
        amountInCents,
        note: note.trim() || undefined,
        paymentMethodLabel: selectedMethod.mutationLabel,
        receivedAtUtcMilli: localDateInputToUtcMilli(paymentDate),
        reference: reference.trim() || undefined,
      });

      if (succeeded) {
        setConfirmOpen(false);
        setSuccessOpen(true);
      }
    } catch (error) {
      setConfirmOpen(false);
      setLocalPaymentError(
        getUserFacingErrorText(error, PAYMENT_RECORD_UNCONFIRMED_TEXT),
      );
    } finally {
      manualPaymentInFlightRef.current = false;
    }
  };

  const visibleActionError =
    cardPaymentExperienceActive && cardCheckoutStartError ? null : actionError;

  const paymentAlerts = (
    <>
      {loading && !detail ? (
        <div className="flex items-center gap-3 text-sm font-semibold text-[#4B5563]">
          <CircularProgress size={20} />
          Loading invoice payment details...
        </div>
      ) : null}
      {visibleActionError || localPaymentError ? (
        <Alert severity="error">
          {localPaymentError || visibleActionError}
        </Alert>
      ) : null}
      {payerDataWarning ? (
        <Alert severity="warning">{payerDataWarning}</Alert>
      ) : null}
      {rules?.creditCardAllowed &&
      cardCheckoutUnavailableReason &&
      paymentCapabilities.manualPayment.available ? (
        <Alert severity="warning">
          Card payment is not available for this invoice right now. You can
          still select another payment method. {cardCheckoutUnavailableReason}
        </Alert>
      ) : null}
      {cardCheckoutRestriction ? (
        <Alert severity="warning">
          <div>{cardCheckoutRestriction.guidance}</div>
          <Button
            color="inherit"
            onClick={() =>
              onOpenEventSales(cardCheckoutRestriction.destination)
            }
            size="small"
            sx={{ fontWeight: 700, mt: 0.5, px: 0, textAlign: "left" }}
          >
            {cardCheckoutRestriction.linkLabel}
          </Button>
        </Alert>
      ) : null}
    </>
  );

  // Design ProcessPaymentModal.jsx:78-146: Payment Date and Amount to Pay sit
  // above the methods for every method. Stripe records a card payment when
  // the card is charged, so the card Payment Date shows today, read-only.
  const renderPaymentDateField = (cardPayment: boolean) => {
    const value = cardPayment ? formatDateInput(Date.now()) : paymentDate;
    return (
      <div>
        <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
          Payment Date
        </p>
        <CalendarDateInput
          ariaLabel="Payment Date"
          mode="fullDate"
          iconPosition="left"
          disabled={cardPayment}
          onChange={setPaymentDate}
          value={value}
        />
      </div>
    );
  };

  const paymentDetails = eventPaymentRestricted ? (
    <div className="rounded-lg border border-[#D7DBE0] p-3 text-sm leading-5 text-[#4B5563]">
      Take payments for ticket invoices in Events &amp; Sponsorships &gt;
      Manage Tickets, and for sponsorship invoices in Events &amp; Sponsorships
      &gt; Manage Sponsorship.
    </div>
  ) : cardPaymentExperienceActive ? (
    <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
      {renderPaymentDateField(true)}
      <div>
        <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
          Amount to Pay
        </p>
        <TextField
          disabled={cardCheckoutControlsLocked}
          error={Boolean(cardAmountError)}
          fullWidth
          inputProps={{ "aria-label": "Amount to Pay" }}
          onChange={({ target }) => setAmountInput(target.value)}
          sx={processPaymentFieldSx}
          value={amountInput}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <p className="mt-1.5 text-xs leading-4 text-[#4B5563]">
          Maximum eligible card payment:{" "}
          {formatCurrency(eligibleCardAmountInCents)}
        </p>
      </div>
    </div>
  ) : (
    <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
      {renderPaymentDateField(false)}
      <div>
        <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
          Amount to Pay
        </p>
        <TextField
          fullWidth
          onChange={({ target }) => setAmountInput(target.value)}
          sx={processPaymentFieldSx}
          value={amountInput}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </div>
    </div>
  );

  // Card payments save Internal Notes on the open card checkout just before
  // the card is submitted; the payment record then shows the note.
  const internalNotesField = (
    <div>
      <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
        Internal Notes
      </p>
      <TextField
        disabled={cardPaymentExperienceActive && cardCheckoutControlsLocked}
        fullWidth
        minRows={2}
        multiline
        onChange={({ target }) => setNote(target.value)}
        placeholder="Additional details for bookkeeping..."
        sx={processPaymentFieldSx}
        value={note}
      />
    </div>
  );

  const cardCheckoutContent = activeCardCheckoutHandoff ? (
    <SecureCardCheckoutHandoff
      amountInCents={activeCardCheckoutHandoff.amountInCents}
      beforeSubmit={internalNotesField}
      onBeforeConfirm={saveCardNoteBeforeConfirm}
      checkoutMode={activeCardCheckoutHandoff.checkoutMode}
      checkoutSessionId={activeCardCheckoutHandoff.checkoutSessionId}
      checkoutUrl={activeCardCheckoutHandoff.checkoutUrl}
      clientSecret={activeCardCheckoutHandoff.clientSecret}
      connectedAccountId={activeCardCheckoutHandoff.connectedAccountId}
      expiresAtUtcMilli={activeCardCheckoutHandoff.expiresAtUtcMilli}
      onPaymentSubmitted={onCardPaymentSubmitted}
      onPaymentProcessed={() => setSuccessOpen(true)}
      onRefreshStatus={onRefreshCardCheckoutStatus}
      onRequestHostedFallback={onRequestHostedFallback}
      publishableKey={activeCardCheckoutHandoff.publishableKey}
      refreshingStatus={loading}
      onPaymentInProgressChange={setCardPaymentInProgress}
      requestingHostedFallback={startingCardCheckout}
      returnUrl={cardCheckoutReturnUrl}
      status={effectiveCardCheckoutStatus}
    />
  ) : cardAmountError ? (
    <Alert data-testid="secure-card-amount-error" severity="error">
      {cardAmountError}
    </Alert>
  ) : cardCheckoutStartError ? (
    <div
      className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4"
      data-testid="secure-card-start-error"
    >
      <Alert severity="error">{actionError || cardCheckoutStartError}</Alert>
      <Button
        disabled={startingCardCheckout}
        onClick={handleRetryCardCheckout}
        sx={{
          ...invoiceModalActionSx({
            backgroundColor: invoiceDesignTokens.blue,
            hoverBackgroundColor: "#0079D6",
          }),
          width: { sm: "auto", xs: "100%" },
        }}
        variant="contained"
      >
        Retry secure card form
      </Button>
    </div>
  ) : (
    <div className="flex min-h-28 items-center justify-center gap-3 rounded-lg border border-[#D7DBE0] bg-[#F9FAFB] p-5 text-sm font-semibold text-[#4B5563]">
      <CircularProgress size={20} />
      Loading secure card form...
    </div>
  );

  // The secure card panel has its own heading and Internal Notes slot.
  const cardPanelShowsHeading = Boolean(activeCardCheckoutHandoff);
  const afterPaymentMethods = eventPaymentRestricted ? null : (
    <div className="space-y-3">
      {/* The card panel has its own CARD INFORMATION heading and shows
          Internal Notes above its Confirm & Record Payment (design). While it
          loads or fails, this heading stands in for it. */}
      {cardPaymentExperienceActive ? (
        cardPanelShowsHeading ? (
          cardCheckoutContent
        ) : (
          <div className="space-y-2.5">
            <p className="text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
              Card Information
            </p>
            {cardCheckoutContent}
          </div>
        )
      ) : (
        <div>
          <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
            Reference Number (Cheque # or Transaction ID)
          </p>
          <TextField
            fullWidth
            onChange={({ target }) => setReference(target.value)}
            placeholder="Enter Reference number and details"
            sx={processPaymentFieldSx}
            value={reference}
          />
        </div>
      )}
      {cardPaymentExperienceActive && cardPanelShowsHeading ? null : internalNotesField}
    </div>
  );

  return (
    <>
      <ProcessPaymentDialogShell
        afterPaymentMethods={afterPaymentMethods}
        alerts={paymentAlerts}
        balanceOwing={formatCurrency(outstandingBalance)}
        billTo={
          getBillToSnapshot(detailForRow)?.customerName ||
          row?.customerName ||
          ""
        }
        busy={dialogLocked}
        emptyPaymentMethodMessage={paymentUnavailableReason}
        invoiceNumber={row?.invoiceNumber || ""}
        onClose={onClose}
        onConfirm={
          eventPaymentRestricted || cardPaymentExperienceActive
            ? undefined
            : () => {
                if (requireManualPaymentConfirmation) {
                  setConfirmOpen(true);
                  return;
                }

                void handleConfirmPayment();
              }
        }
        onPaymentMethodChange={(methodId) => {
          if (cardCheckoutControlsLocked) {
            return;
          }

          setLocalPaymentError(null);
          setCardCheckoutStartError(null);
          setSelectedMethodLabel(methodId);
        }}
        open={open && !confirmOpen && !successOpen}
        paymentDetails={paymentDetails}
        paymentMethods={
          eventPaymentRestricted
            ? []
            : methodOptions.map((method) => ({
                disabled: cardCheckoutControlsLocked,
                id: method.mutationLabel,
                label: method.displayLabel,
              }))
        }
        primaryActionDisabled={
          !selectedMethod ||
          processing ||
          amountInCents <= 0 ||
          amountInCents > outstandingBalance
        }
        selectedPaymentMethodId={selectedMethodLabel}
      />

      <ManageInvoiceActionConfirmationDialog
        customerName={row?.customerName || ""}
        errorMessage={localPaymentError}
        kind="PROCESS_PAYMENT"
        loading={processing}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmPayment}
        open={
          requireManualPaymentConfirmation &&
          confirmOpen &&
          !eventPaymentRestricted
        }
      />

      <PaymentProcessedDialog
        actionLoading={receiptActionLoading}
        billingContactEmail={receiptBillingSnapshot?.contactEmail}
        customerName={
          receiptBillingSnapshot?.customerName ||
          row?.customerName ||
          "Customer"
        }
        onClose={() => {
          setSuccessOpen(false);
          onClose();
        }}
        onEmailReceipt={onEmailReceipt}
        onPrintReceipt={onPrintReceipt}
        open={successOpen}
      />
    </>
  );
};

export const PaymentProcessedDialog = ({
  actionLoading = false,
  billingContactEmail,
  customerName,
  onClose,
  onEmailReceipt,
  onPrintReceipt,
  open,
}: {
  actionLoading?: boolean;
  billingContactEmail?: string | null;
  customerName: string;
  onClose: () => void;
  onEmailReceipt?: () => Promise<void> | void;
  onPrintReceipt?: () => Promise<void> | void;
  open: boolean;
}) => {
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [localActionError, setLocalActionError] = useState<string | null>(null);
  const actionInFlightRef = useRef(false);
  const effectiveActionLoading = actionLoading || activeActionKey !== null;
  // Design D:1498690: one note, then Print Receipt and Email Receipt. Email
  // Receipt is also the way to send the receipt again.
  const actions = [
    onPrintReceipt
      ? { key: "print", label: "Print Receipt", onClick: onPrintReceipt }
      : null,
    onEmailReceipt
      ? { key: "email", label: "Email Receipt", onClick: onEmailReceipt }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    onClick: () => Promise<void> | void;
  }>;

  useEffect(() => {
    if (open) {
      setActiveActionKey(null);
      setLocalActionError(null);
      actionInFlightRef.current = false;
    }
  }, [open]);

  const handleAction = async (action: (typeof actions)[number]) => {
    if (effectiveActionLoading || actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setActiveActionKey(action.key);
    setLocalActionError(null);
    try {
      await action.onClick();
    } catch (error) {
      setLocalActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("finish this receipt action"),
        ),
      );
    } finally {
      actionInFlightRef.current = false;
      setActiveActionKey(null);
    }
  };

  return (
    <Dialog
      open={open}
      PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 440 }) }}
      onClose={effectiveActionLoading ? undefined : onClose}
    >
      <div
        className="relative bg-white px-7 py-[30px] text-center"
        data-testid="payment-processed-dialog"
        style={{ fontFamily: invoiceDesignFontFamily }}
      >
        <InvoiceDialogCloseButton
          ariaLabel="Close payment success"
          disabled={effectiveActionLoading}
          onClick={onClose}
        />
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#12B981] text-white">
          <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />
        </div>
        <div className="mx-auto mt-[18px] max-w-[340px] text-[14px] font-normal leading-[1.55] text-[#0A0A0A]">
          Your payment has been processed successfully!
        </div>
        <p className="mx-auto mt-2.5 max-w-[340px] text-[14px] font-normal leading-[1.55] text-[#0A0A0A]">
          {onEmailReceipt ? (
            <>
              The email receipt will be sent to{" "}
              <span className="font-semibold">
                {billingContactEmail || "the company billing contact"}
              </span>
              , the billing contact at{" "}
              <span className="font-semibold">{customerName}</span> who made
              the payment.
            </>
          ) : (
            <>
              A payment receipt is ready for{" "}
              <span className="font-semibold">{customerName}</span>.
            </>
          )}
        </p>
        {localActionError ? (
          <Alert className="mt-4 text-left" severity="error">
            {localActionError}
          </Alert>
        ) : null}
        {actions.length ? (
          <div className="mt-[22px] flex w-full flex-wrap justify-center gap-3">
            {actions.map((action) => (
              <Button
                disabled={effectiveActionLoading}
                key={action.key}
                onClick={() => void handleAction(action)}
                startIcon={
                  activeActionKey === action.key ? (
                    <CircularProgress color="inherit" size={15} />
                  ) : undefined
                }
                sx={{
                  ...invoiceModalActionSx({
                    backgroundColor: invoiceDesignTokens.gold,
                    hoverBackgroundColor: invoiceDesignTokens.goldDark,
                  }),
                  flex: "1 1 0",
                  maxWidth: 170,
                  minHeight: 44,
                }}
                variant="contained"
              >
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

// INV-PREVIEW-003: shared green-check success dialog. It renders above the
// fullscreen invoice preview after a successful action, as the Claude Design
// ActionSuccessModal.
export const InvoiceSuccessDialog = ({
  highlights,
  message,
  note,
  onClose,
  open,
}: {
  highlights?: readonly string[];
  message: string;
  note?: React.ReactNode;
  onClose: () => void;
  open: boolean;
}) => (
  <ActionSuccessDialog
    highlights={highlights}
    message={open ? message : null}
    note={note}
    onClose={onClose}
  />
);
